import { useSearchParams } from 'next/navigation';
import { Fragment, useId } from 'react';

import cn from 'classnames';
import { minBy } from 'lodash';
import { createSelector } from 'reselect';

import type { TCabinPrice } from '@/helpers/api/graphql/queries/cabinCategoriesAvailability';

import { Amount } from '@/components/elements/Price';
import { Skeleton } from '@/components/Skeleton';
import Tooltip, { TooltipMode } from '@/components/Tooltip';
import UIResource from '@/components/UIResource';
import UIResourceHtml from '@/components/UIResource/Html';
import UIResourceImage from '@/components/UIResource/Image';
import { selectCabins, selectCurrencyCode, selectPriceType, selectSailors } from '@/ducks/filters/selectors';
import { selectIsSailingDataLoaded, selectSailingData } from '@/ducks/voyageInfo/sailing/selectors';
import { useIsPromoActive } from '@/features/promo/lib/useIsPromoActive';
import PrecheckoutPromoBox from '@/features/promo/PrecheckoutPromoBox';
import {
  selectCabinCategories,
  selectIsCabinCategoriesIsLoading,
  selectIsCabinCategoriesLoaded,
} from '@/reducers/voyagePlanner/chooseCabin/cabinCategories/selectors';
import { useAppSelector } from '@/store';

import { getDiscountValues } from './helpers';
import ModuleCard from './ModuleCard';

import styles from './CabinDescriptors.module.scss';

const selectAvailableCabinCategories = createSelector(selectCabinCategories, (cabinCategories) => {
  const allAvailableCabinCategories = cabinCategories?.filter?.((cabinCategory) => cabinCategory.isAvailable) ?? [];
  const availableNonVipCategories = allAvailableCabinCategories.filter((cabinCategory) => !cabinCategory.isVip);
  const availableVipCategories = allAvailableCabinCategories.filter((cabinCategory) => cabinCategory.isVip);
  const cheapestAvailableCategory = minBy(allAvailableCabinCategories, 'lowestSubCategoryPrice.totalPrice.amount');

  return {
    allAvailableCabinCategories,
    availableNonVipCategories,
    availableVipCategories,
    cheapestAvailableCategory,
  };
});

const PriceBreakdownPopover = ({ price, sailors }: { price?: TCabinPrice; sailors: number }) => {
  const id = useId();

  // TODO: Taxes & Fees field should be added, whenever API will provide it (https://virginvoyages.atlassian.net/browse/MSH-135556)
  if (!price) return null;

  const { amount, currencyCode, discount, originalAmount } = price;

  const data = [
    {
      id: `${id}-1`,
      label: (
        <UIResource
          id="CabinDescriptor.priceBreakdown.cabinSailors"
          values={{
            M: sailors,
            N: 1,
          }}
        />
      ),
      value: <Amount amount={originalAmount} currencyCode={currencyCode} />,
    },
    {
      id: `${id}-2`,
      label: <UIResource id="CabinDescriptor.priceBreakdown.discounts" />,
      value: <Amount amount={-discount} currencyCode={currencyCode} />,
    },
    {
      id: `${id}-3`,
      isTotal: true,
      label: <UIResource id="CabinDescriptor.priceBreakdown.total" />,
      value: <Amount amount={amount} currencyCode={currencyCode} />,
    },
  ];

  return (
    <Tooltip
      align="end"
      aria-label="Detailed price breakdown"
      isInline
      mode={TooltipMode.INFO}
      popupClassName={styles.priceBreakdown}
      portal
      side="bottom"
    >
      <span className="sr-only">
        {data.map(({ id, label, value }) => (
          <Fragment key={id}>
            {label}:{value}.
          </Fragment>
        ))}
      </span>
      <table aria-hidden className={styles.priceBreakdownTable}>
        <tbody>
          {data.map(({ id, isTotal, label, value }) => (
            <tr
              className={cn(styles.priceBreakdownTableRow, {
                [styles.priceBreakdownTableRowTotal as string]: isTotal,
              })}
              key={id}
            >
              <th>{label}</th>
              <td>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Tooltip>
  );
};

const CabinDescriptors = () => {
  const searchParams = useSearchParams();
  const isCabinCategoriesLoaded = useAppSelector(selectIsCabinCategoriesLoaded);
  const isCabinCategoriesIsLoading = useAppSelector(selectIsCabinCategoriesIsLoading) ?? true;
  const isSailingDataLoaded = useAppSelector((state) => selectIsSailingDataLoaded(state, searchParams));
  const isLoading = !isSailingDataLoaded || !isCabinCategoriesLoaded || isCabinCategoriesIsLoading;

  const priceType = useAppSelector(selectPriceType);
  const sailors = useAppSelector(selectSailors) ?? 2;
  const cabins = useAppSelector(selectCabins);
  const currencyCode = useAppSelector(selectCurrencyCode);
  const sailingData = useAppSelector(selectSailingData);

  const { availableNonVipCategories, availableVipCategories, cheapestAvailableCategory } =
    useAppSelector(selectAvailableCabinCategories);
  const cheapestAvailableUnitPrices = cheapestAvailableCategory?.lowestSubCategoryPrice.totalPrice;

  const cabinCategories = useAppSelector(selectCabinCategories);
  const { isCabinAndSuitesDiscountAvailable, isCabinDiscountAvailable, isSuitesDiscountAvailable } = getDiscountValues(
    cabinCategories ?? [],
    currencyCode,
  );
  const isDiscountAvailable =
    isCabinAndSuitesDiscountAvailable || isCabinDiscountAvailable || isSuitesDiscountAvailable;
  const isPromoActive = useIsPromoActive('promoBox', sailingData!);
  const specializedMerchandisingModuleAvailable = isDiscountAvailable && isPromoActive;

  const sharedModuleCardsProps = {
    cabins,
    currencyCode,
    duration: sailingData?.duration,
    isLoading,
    priceType,
  };

  return (
    <section className={styles.cabinDescriptors}>
      <div className={styles.wrapper}>
        <UIResourceImage className={styles.doorIcon} resourceId="CabinDescriptor.door.icon" role="presentation" />

        <h2 className={styles.heading}>
          <UIResource id="CabinDescriptor.heading" />
        </h2>
        <div className={styles.body}>
          <UIResourceHtml id="CabinDescriptor.body" />
        </div>
        <div className={styles.prompt}>
          <UIResourceHtml id="CabinDescriptor.chooseCabin.prompt" />
        </div>
        <div className={styles.moduleList}>
          <ModuleCard availableUnits={availableNonVipCategories} {...sharedModuleCardsProps} />
          <ModuleCard availableUnits={availableVipCategories} isVip {...sharedModuleCardsProps} />
        </div>
        {specializedMerchandisingModuleAvailable && (
          <PrecheckoutPromoBox
            className={styles.promoModule}
            isPayInFullAvailable={false}
            voyageDuration={sailingData?.duration ?? 0}
          />
        )}
      </div>
      <div className={styles.footer}>
        {isLoading ? (
          <Skeleton inline maxWidth="375px" width="90%" />
        ) : (
          <div className={styles.wrapper}>
            <span>
              <UIResource
                id="CabinDescriptor.estimatedTotal.banner"
                values={{
                  Amount: '',
                  Currency: '',
                  N: sailors,
                }}
              />{' '}
              <span className={styles.footerPrice}>
                <Amount amount={cheapestAvailableUnitPrices?.amount ?? 0} currencyCode={currencyCode} />
              </span>
            </span>

            <PriceBreakdownPopover price={cheapestAvailableUnitPrices} sailors={sailors} />
          </div>
        )}
      </div>
    </section>
  );
};

export default CabinDescriptors;
